import { useScrollAnimation } from '@/hooks/useScrollAnimation';


const steps = [
  {
    number: '01',
    title: 'IDEA',
    description: 'Escucho tu visión, entiendo tu marca y defino los objetivos del proyecto.',
    icon: 'ri-lightbulb-line',
    color: '#FFD60A',
    bg: 'rgba(255,214,10,0.15)',
  },
  {
    number: '02',
    title: 'DISEÑO',
    description: 'Creo propuestas visuales creativas alineadas con tu identidad y objetivos.',
    icon: 'ri-pencil-ruler-2-line',
    color: '#00D9FF',
    bg: 'rgba(0,217,255,0.15)',
  },
  {
    number: '03',
    title: 'PRODUCCIÓN',
    description: 'Refino cada detalle, aplico correcciones y preparo los archivos finales.',
    icon: 'ri-settings-3-line',
    color: '#FF006E',
    bg: 'rgba(255,0,110,0.15)',
  },
  {
    number: '04',
    title: 'ENTREGA',
    description: 'Entrego los archivos listos para usar en todos los formatos que necesitas.',
    icon: 'ri-rocket-line',
    color: '#06FFA5',
    bg: 'rgba(6,255,165,0.15)',
  },
];

export default function Process() {
  const ref = useScrollAnimation();

  return (
    <section id="process" className="py-24 md:py-32 relative bg-[#050505] overflow-hidden">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-5 pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(circle, #ffffff 1px, transparent 1px)',
          backgroundSize: '40px 40px',
        }}
      ></div>

      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-gradient-to-b from-[#FF6B35]/8 to-transparent blur-3xl pointer-events-none"></div>

      <div ref={ref} className="relative z-[3] max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="fade-in-up flex items-center gap-4 mb-6">
          <span className="text-[#FF6B35] font-bebas text-lg tracking-widest">05</span>
          <div className="w-12 h-px bg-[#FF6B35]"></div>
          <span className="text-white/40 text-sm tracking-widest uppercase">Proceso</span>
        </div>

        <div className="fade-in-up text-center mb-20">
          <h2 className="font-bebas text-5xl md:text-6xl lg:text-7xl text-white leading-tight">
            MI <span className="gradient-text">PROCESO</span>
          </h2>
          <p className="text-white/50 font-poppins text-base mt-4 max-w-lg mx-auto">
            Un flujo de trabajo claro y eficiente para garantizar resultados de calidad en cada proyecto.
          </p>
        </div>

        {/* Desktop timeline */}
        <div className="hidden md:block relative">
          {/* Line */}
          <div className="absolute top-[60px] left-0 right-0 h-px bg-white/10 z-0">
            <div className="h-full bg-gradient-to-r from-[#FFD60A] via-[#00D9FF] via-[#FF006E] to-[#06FFA5] animate-draw-line"></div>
          </div>

          <div className="grid grid-cols-4 gap-8 relative z-10">
            {steps.map((step, i) => (
              <div
                key={step.title}
                className="fade-in-up flex flex-col items-center text-center"
                style={{ transitionDelay: `${i * 150}ms` }}
              >
                {/* Node */}
                <div
                  className="w-[120px] h-[120px] flex flex-col items-center justify-center rounded-full border-2 mb-8 relative"
                  style={{ borderColor: step.color, backgroundColor: step.bg }}
                >
                  <span className="font-bebas text-2xl" style={{ color: step.color }}>{step.number}</span>
                  <i className={`${step.icon} text-xl mt-1`} style={{ color: step.color }}></i>
                  {/* Pulse ring */}
                  <div
                    className="absolute inset-0 rounded-full animate-ping opacity-20"
                    style={{ backgroundColor: step.color }}
                  ></div>
                </div>

                <h3 className="font-bebas text-3xl text-white mb-3">{step.title}</h3>
                <p className="text-white/50 text-sm font-poppins leading-relaxed max-w-[180px]">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Mobile timeline */}
        <div className="md:hidden space-y-8">
          {steps.map((step, i) => (
            <div
              key={step.title}
              className="fade-in-up flex gap-6 items-start"
              style={{ transitionDelay: `${i * 100}ms` }}
            >
              <div className="flex flex-col items-center">
                <div
                  className="w-16 h-16 flex flex-col items-center justify-center rounded-full border-2 flex-shrink-0"
                  style={{ borderColor: step.color, backgroundColor: step.bg }}
                >
                  <i className={`${step.icon} text-xl`} style={{ color: step.color }}></i>
                </div>
                {i < steps.length - 1 && (
                  <div className="w-px flex-1 mt-2" style={{ backgroundColor: `${step.color}30`, minHeight: '40px' }}></div>
                )}
              </div>
              <div className="pt-2">
                <div className="flex items-center gap-3 mb-2">
                  <span className="font-bebas text-sm" style={{ color: step.color }}>{step.number}</span>
                  <h3 className="font-bebas text-2xl text-white">{step.title}</h3>
                </div>
                <p className="text-white/50 text-sm font-poppins leading-relaxed">{step.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="fade-in-up text-center mt-20">
          <p className="text-white/40 font-poppins text-sm mb-6">¿Listo para empezar tu proyecto?</p>
          <a
            href="https://wa.me/573024819377?text=Hola%20Michael%2C%20quiero%20iniciar%20un%20proyecto"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 bg-gradient-to-r from-[#FF6B35] to-[#FF006E] text-white font-semibold px-8 py-4 rounded-full hover:scale-105 transition-all duration-300 text-base whitespace-nowrap"
          >
            <i className="ri-whatsapp-line text-lg"></i>
            Iniciar proyecto ahora
          </a>
        </div>
      </div>
    </section>
  );
}
